import React from "react";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Box from "@amzn/awsui-components-react/polaris/box";
import Header from "@amzn/awsui-components-react/polaris/header";
import Link from "@amzn/awsui-components-react/polaris/link";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import StatusIndicator, {
  StatusIndicatorProps,
} from "@amzn/awsui-components-react/polaris/status-indicator";
import { useSearchDocumentsQuery } from "components/documents-table-os/endpoints";
import { buildPropertyFilterQuery } from "components/documents-table-os/buildPropertyFilterQuery";
import {
  OUTCOME_REQUIREMENT_TYPES,
  VALUE_REQUIREMENT_TYPES,
  ClosingRequirementResult,
} from "components/closing-requirements/definitions";
import { cap } from "utils/date-format";
import RequirementValueInput from "./RequirementValueInput";
import { IClosingRequirementsChecklistProps } from "./definitions";
import { toFileOptions } from "./utils";

const indicatorType = (
  result: ClosingRequirementResult,
): StatusIndicatorProps.Type => {
  if (result.met) return "success";
  return result.severity === "BLOCK" ? "error" : "warning";
};

const ClosingRequirementsChecklist: React.FC<
  IClosingRequirementsChecklistProps
> = ({
  category,
  teamName,
  recordId,
  results,
  requirements,
  values,
  onValueChange,
  isEvaluating,
  evaluateError,
  onViewOutcomes,
  onViewFiles,
}) => {
  const hasFileRequirement = (results ?? []).some(
    (result) => result.requirementType === "FILE",
  );
  const documents = useSearchDocumentsQuery(
    {
      teamName,
      propertyFilterQuery: buildPropertyFilterQuery({
        gaiaObjectId: recordId,
        parentId: recordId,
        associatedEventIds: [],
        existingTokens: [],
        existingOperation: "and",
      }),
      pageSize: 100,
      sortBy: "fileName",
      sortOrder: "asc",
      excludeFields: ["summary"],
    },
    { skip: !hasFileRequirement || !recordId },
  );
  const fileOptions = toFileOptions(documents.data?.documents);

  return (
    <SpaceBetween size="s">
      <Header
        variant="h3"
        description={`Set by your team in Team settings. Requirements marked block must be met before this ${category} can be completed.`}
      >
        {`${cap(category)} closing requirements`}
      </Header>
      {evaluateError && (
        <Alert type="warning" header="Couldn't check the closing requirements">
          {`${evaluateError} You can still save; Gaia checks the requirements when it saves.`}
        </Alert>
      )}
      {!results && isEvaluating && (
        <StatusIndicator type="loading">
          Checking closing requirements
        </StatusIndicator>
      )}
      {(results ?? []).map((result) => {
        const requirement = requirements[result.requirementId];
        const outcomeLink =
          !result.met &&
          onViewOutcomes &&
          OUTCOME_REQUIREMENT_TYPES.includes(result.requirementType) ? (
            <Link
              variant="primary"
              fontSize="body-s"
              onFollow={(e) => {
                e.preventDefault();
                onViewOutcomes();
              }}
            >
              Go to outcomes
            </Link>
          ) : null;
        return (
          <SpaceBetween size="xxs" key={result.requirementId}>
            <StatusIndicator type={indicatorType(result)}>
              {result.title}
            </StatusIndicator>
            {requirement?.description && (
              <Box variant="small">{requirement.description}</Box>
            )}
            {!result.met && result.reason && (
              <Box
                variant="small"
                color={
                  result.severity === "BLOCK"
                    ? "text-status-error"
                    : "text-status-warning"
                }
              >
                {result.reason}
                {outcomeLink && <> {outcomeLink}</>}
              </Box>
            )}
            {!result.reason && outcomeLink}
            {VALUE_REQUIREMENT_TYPES.includes(result.requirementType) && (
              <RequirementValueInput
                result={result}
                requirement={requirement}
                value={values[result.requirementId]}
                onValueChange={(value) =>
                  onValueChange(result.requirementId, value)
                }
                fileOptions={fileOptions}
                isLoadingFiles={documents.isFetching}
                onRefreshFiles={() => documents.refetch()}
                onViewFiles={onViewFiles}
              />
            )}
          </SpaceBetween>
        );
      })}
    </SpaceBetween>
  );
};

export default ClosingRequirementsChecklist;
